import { env } from "@beztack/env/ui";
import { urlBase64ToUint8Array } from "@/lib/pwa/vapid-key";

/**
 * Customer-side Web Push registration (issue #93).
 *
 * A customer — signed in or a guest — opts into notifications for ONE delivery
 * Order. The subscription is sent to the API with that `orderId`, which is what
 * lets a guest be notified at all: the row belongs to the Order rather than to an
 * account, and the API authorizes it exactly as it authorizes reading the Order's
 * tracking page.
 *
 * Scoped to `/m/` — the public storefront — so it never overlaps the Rider worker
 * at `/rider/`.
 */
const CUSTOMER_SW_PATH = "/customer-sw.js";
const CUSTOMER_SW_SCOPE = "/m/";

/** Why an opt-in could not happen, for copy the customer can act on. */
export type OrderPushFailure = "denied" | "failed" | "unconfigured" | "unsupported";

export type OrderPushResult = { ok: true } | { ok: false; reason: OrderPushFailure };

export function isOrderPushSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

/**
 * Which Orders this browser has opted into.
 *
 * The durable record is the `push_subscription` row on the server; this is only
 * so the button renders in the right state on a return visit without a round
 * trip. Losing it silences nothing.
 */
const OPT_IN_STORAGE_PREFIX = "beztack:order-push:";

function optInKey(orderId: string): string {
  return `${OPT_IN_STORAGE_PREFIX}${orderId}`;
}

export function hasOptedIntoOrderPush(orderId: string): boolean {
  try {
    return window.localStorage.getItem(optInKey(orderId)) === "1";
  } catch {
    return false;
  }
}

function rememberOptIn(orderId: string, value: boolean): void {
  try {
    if (value) {
      window.localStorage.setItem(optInKey(orderId), "1");
    } else {
      window.localStorage.removeItem(optInKey(orderId));
    }
  } catch {
    // A browser with storage blocked can still receive notifications; only the
    // button's remembered state is lost.
  }
}

export async function registerCustomerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!isOrderPushSupported()) {
    return null;
  }
  try {
    return await navigator.serviceWorker.register(CUSTOMER_SW_PATH, {
      scope: CUSTOMER_SW_SCOPE,
    });
  } catch (error) {
    console.error("Failed to register customer service worker", error);
    return null;
  }
}

function apiBase(): string {
  return env.VITE_API_URL.replace(/\/$/, "");
}

/**
 * Opt this browser into notifications for one Order.
 *
 * Returns a reason rather than throwing, so the caller can tell the customer
 * whether to grant the permission, or that their browser cannot do this at all.
 */
export async function ensureOrderPushSubscription(orderId: string): Promise<OrderPushResult> {
  if (!isOrderPushSupported()) {
    return { ok: false, reason: "unsupported" };
  }
  if (!env.VITE_VAPID_PUBLIC_KEY) {
    return { ok: false, reason: "unconfigured" };
  }

  const registration = await registerCustomerServiceWorker();
  if (!registration) {
    return { ok: false, reason: "unsupported" };
  }

  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    return { ok: false, reason: "denied" };
  }

  try {
    const subscription =
      (await registration.pushManager.getSubscription()) ??
      (await registration.pushManager.subscribe({
        applicationServerKey: urlBase64ToUint8Array(env.VITE_VAPID_PUBLIC_KEY),
        userVisibleOnly: true,
      }));

    const response = await fetch(`${apiBase()}/api/push/subscribe`, {
      body: JSON.stringify({ ...subscription.toJSON(), orderId }),
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      method: "POST",
    });

    if (!response.ok) {
      return { ok: false, reason: "failed" };
    }

    rememberOptIn(orderId, true);
    return { ok: true };
  } catch (error) {
    console.error("Failed to subscribe to order notifications", error);
    return { ok: false, reason: "failed" };
  }
}

/** Stop notifications for one Order, leaving any other subscription alone. */
export async function removeOrderPushSubscription(orderId: string): Promise<boolean> {
  rememberOptIn(orderId, false);

  if (!isOrderPushSupported()) {
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.getRegistration(CUSTOMER_SW_SCOPE);
    const subscription = await registration?.pushManager.getSubscription();
    if (!subscription) {
      return false;
    }

    const response = await fetch(`${apiBase()}/api/push/unsubscribe`, {
      body: JSON.stringify({ endpoint: subscription.endpoint, orderId }),
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      method: "DELETE",
    });

    return response.ok;
  } catch (error) {
    console.error("Failed to unsubscribe from order notifications", error);
    return false;
  }
}
